import React, { memo, useCallback, useMemo } from 'react'

import useEnhancedModelCompatibility from 'Pods/ThreeDStudio/Hooks/useEnhancedModelCompatibility'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import ActionSelector from 'Pods/ThreeDStudio/Shared/ActionSelector/ActionSelector'
import AIEngineSelector from 'Pods/ThreeDStudio/Shared/AIEngineSelector/AIEngineSelector'
import GenerateArea from 'Pods/ThreeDStudio/Shared/GenerateArea/GenerateArea'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setInputImage, setPrompt } from 'Pods/ThreeDStudio/Store/actions/textureSettings'
import { setActiveTextureActionTab } from 'Pods/ThreeDStudio/Store/actions/ui'
import { TEXTURE_ACTION_TAB } from 'Pods/ThreeDStudio/Store/constants'

import MaterialSelector from './Components/MaterialSelector/MaterialSelector'
import Preview from './Components/Preview/Preview'
import TextureQualitySelector from './Components/TextureQualitySelector/TextureQualitySelector'

function TextureTab() {
  const t = useTranslation()
  const isImageUploading = useThreeDStudioStore(state => state.ui?.isImageUploading)
  const activeTextureActionTab = useThreeDStudioStore(state => state.ui?.activeTextureActionTab)
  const prompt = useThreeDStudioStore(state => state.textureSettings.prompt)
  const inputImage = useThreeDStudioStore(state => state.textureSettings.inputImage)

  const { isModelIncompatible, tooltipMessage } = useEnhancedModelCompatibility()
  const isMissingRequiredParams = !prompt && !inputImage

  const handleImageTabClick = useCallback(() => {
    setActiveTextureActionTab(TEXTURE_ACTION_TAB.IMAGE)
    setPrompt('')
  }, [])

  const handleTextTabClick = useCallback(() => {
    setActiveTextureActionTab(TEXTURE_ACTION_TAB.TEXT)
    setInputImage(undefined)
  }, [])

  const tabs = useMemo(() => ({
    image: {
      id: TEXTURE_ACTION_TAB.IMAGE,
      onClick: handleImageTabClick,
      isActive: activeTextureActionTab === TEXTURE_ACTION_TAB.IMAGE,
      setInputImage,
    },
    text: {
      id: TEXTURE_ACTION_TAB.TEXT,
      onClick: handleTextTabClick,
      isActive: activeTextureActionTab === TEXTURE_ACTION_TAB.TEXT,
      placeholder: t('viewerSidebar.enhance.texture.textPlaceholder'),
      setPrompt,
    },
  }), [t, activeTextureActionTab, handleImageTabClick, handleTextTabClick])

  return (
    <>
      <div className="texture-tab enhance-model-panel__tab">
        <span className="enhance-model-panel__title cgt-heading-50">{t('viewerSidebar.enhance.texture.title')}</span>
        <div className="enhance-model-panel__settings">
          <AIEngineSelector isEnhanced />
          <ActionSelector
            id="texture-tab"
            sliceName="textureSettings"
            storeSchemaKey="enhancedModelSchema"
            tabs={tabs}
          />
          <MaterialSelector />
          <TextureQualitySelector />
        </div>
        <Preview />
      </div>
      <GenerateArea
        generateLabel={t('viewerSidebar.enhance.texture.generateLabel')}
        generatedLabel={t('viewerSidebar.enhance.common.done')}
        generatingLabel={t('viewerSidebar.enhance.common.processing')}
        isEnhancementMode
        isGenerateDisabled={isImageUploading || isModelIncompatible || isMissingRequiredParams}
        tooltipMessage={tooltipMessage}
        withIcon={false}
      />
    </>
  )
}

export default memo(TextureTab)
